import React, { useContext, useEffect, useState } from "react";
import {
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  Text as RNText,
  TextInput,
  TouchableOpacity,
  View,
  Image,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useToast } from "react-native-toast-notifications";
import { useDispatch, useSelector } from "react-redux";
import * as ImagePicker from "expo-image-picker";
import { useAppTheme } from "../../../hooks/useAppTheme";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import {
  SampleRequestDetail,
  SampleRequestStatus,
  UserRole,
  Attachment,
} from "../../../models/sampleRequestModel";
import {
  getSampleRequestById,
  approveSampleRequest,
  rejectSampleRequest,
  requestSampleRequestChanges,
  submitSampleRequestChanges,
  submitSampleRequestToFactory,
  approveSampleRequestCAD,
  submitSampleRequestProduction,
  confirmSampleRequestReceived,
  updateSampleRequest,
} from "../../../redux/dispatcher/sampleRequestDispatcher";
import { EditSampleRequestModal } from "./EditSampleRequestModal";
import { pushFileToS3 } from "../../../services/s3BucketService";
import { translateFields, useTranslation } from "../../../utils/useTranslation";
import {
  sampleRequestActivityPatterns,
  sampleRequestTranslationMap,
  translations,
} from "../../../utils/translations";

export type ActivityRole =
  "Sales" | "Management" | "China" | "Production" | "System";

export interface ChangeRequest {
  requestedBy: string;
  requestedFor: "Sales" | "China";
  remarks: string;
  requestedAt: string;
  resolvedAt?: string | null;
}

export interface SampleInfo {
  customer: string;
  dueDate?: string | null;
  itemName: string;
  sampleQtyNeeded: number;
  descriptionOfSample?: string;
  purpose: string;
  purposeOther?: string;
  attachments?: Attachment[];
}

export interface MouldPreparation {
  drawingProvidedByFactory?: boolean | null;
  toolingRequestBy?: string;
  toolingCost?: string;
  toolingApprovedBy?: string;
  remarks?: string;
  attachments?: Attachment[];
}

export interface DeliveryInfo {
  deliveryContactPerson: string;
  deliveryContactNumber: string;
  deliveryAddress: string;
  remarks?: string;
}

export interface TrackingInfo {
  courier?: string;
  trackingNumber?: string;
  shippedDate?: string;
  attachments?: Attachment[];
}

interface PickedFile {
  id: string;
  fileName: string;
  fileUri: string;
  fileType: string;
}

type Styles = ReturnType<typeof createStyles>;

const sampleRequestS3Folder = () => `sample-requests`;

const statusColorMap: Record<
  string,
  { bg: string; text: string; dot: string }
> = {
  "Pending Approval": { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B" },
  "Changes Requested": { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B" },
  "Sent to Factory": { bg: "#DBEAFE", text: "#1E40AF", dot: "#3B82F6" },
  "Reviewing CAD": { bg: "#DBEAFE", text: "#1E40AF", dot: "#3B82F6" },
  "CAD Changes Requested": { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B" },
  "In Production": { bg: "#DBEAFE", text: "#1E40AF", dot: "#3B82F6" },
  Shipped: { bg: "#E1F0FA", text: "#1D6FA5", dot: "#2F95DC" },
  Completed: { bg: "#D1FAE5", text: "#065F46", dot: "#10B981" },
  Declined: { bg: "#FEE2E2", text: "#991B1B", dot: "#EF4444" },
};

type TranslationKey = keyof typeof translations;

const STATUS_LABEL_OVERRIDES: Partial<
  Record<SampleRequestStatus, TranslationKey>
> = {
  Completed: "sampleStatusClosed",
};

const ALERT_MESSAGES: Partial<Record<SampleRequestStatus, TranslationKey>> = {
  "Pending Approval": "alertPendingApproval",
  "Changes Requested": "alertChangesRequested",
  "Sent to Factory": "alertSentToFactory",
  "Reviewing CAD": "alertReviewingCad",
  "CAD Changes Requested": "alertCadChangesRequested",
  "In Production": "alertInProduction",
  Shipped: "alertShipped",
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatDateLong(input?: string | Date | null): string {
  if (!input) return "-";
  const d = typeof input === "string" ? new Date(input) : input;
  if (isNaN(d.getTime())) return typeof input === "string" ? input : "-";
  const day = String(d.getDate()).padStart(2, "0");
  return `${day} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function formatDateSlash(input?: string | Date | null): string {
  if (!input) return "-";
  const d = typeof input === "string" ? new Date(input) : input;
  if (isNaN(d.getTime())) return typeof input === "string" ? input : "-";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()}`;
}

const dash = (v?: string | number | null) =>
  v === undefined || v === null || v === "" ? "-" : String(v);

const parseDDMMYYYY = (value: string): Date | null => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return d.getDate() === Number(m[1]) && d.getMonth() === Number(m[2]) - 1
    ? d
    : null;
};

// "dhl.com/track/123" → "https://dhl.com/track/123"
const normalizeUrl = (value: string) => {
  const v = value.trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

const isImageFile = (fileName: string) => {
  if (!fileName) return false;
  const lower = fileName.toLowerCase();
  return [".jpg", ".jpeg", ".png", ".gif", ".webp"].some((ext) =>
    lower.endsWith(ext),
  );
};

// ── Module-scope sub-components ─────────────────────────────────────
// These used to be defined inside SampleRequestDetailsScreen. Every
// setState there re-ran the component function, which redefined these
// as brand-new function references, so React treated them as new
// component types and unmounted/remounted their inner elements —
// including TextInputs, which dropped keyboard focus after a single
// character. Hoisting them here keeps their identity stable across
// re-renders. They take styles/colors as props since they no longer
// close over the parent's scope.

const ActionButton = ({
  label,
  tone,
  onPress,
  styles,
}: {
  label: string;
  tone: "approve" | "reject" | "neutral";
  onPress: () => void;
  styles: Styles;
}) => (
  <TouchableOpacity
    style={[
      styles.actionButtonBase,
      tone === "approve" && styles.actionButtonApprove,
      tone === "reject" && styles.actionButtonReject,
      tone === "neutral" && styles.actionButtonNeutral,
    ]}
    onPress={onPress}
  >
    <RNText style={styles.actionButtonText}>{label}</RNText>
  </TouchableOpacity>
);

const RemarksPrompt = ({
  placeholder,
  confirmLabel,
  onConfirm,
  onCancel,
  styles,
  colors,
}: {
  placeholder: string;
  confirmLabel: string;
  onConfirm: (text: string) => void;
  onCancel: () => void;
  styles: Styles;
  colors: any;
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  return (
    <View style={styles.remarksPromptBox}>
      <TextInput
        style={styles.remarksPromptInput}
        placeholder={placeholder}
        placeholderTextColor={colors.textSecondary}
        value={text}
        onChangeText={setText}
        multiline
        numberOfLines={2}
      />
      <View style={styles.remarksPromptActions}>
        <TouchableOpacity style={styles.remarksCancelBtn} onPress={onCancel}>
          <RNText style={styles.remarksCancelText}>{t("cancel")}</RNText>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.remarksConfirmBtn}
          onPress={() => text.trim() && onConfirm(text.trim())}
        >
          <RNText style={styles.remarksConfirmText}>{confirmLabel}</RNText>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const FormField = ({
  label,
  value,
  onChangeText,
  placeholder,
  multiline,
  styles,
  colors,
}: {
  label: string;
  value: string;
  onChangeText: (t: string) => void;
  placeholder?: string;
  multiline?: boolean;
  styles: Styles;
  colors: any;
}) => (
  <View style={styles.formFieldWrapper}>
    <RNText style={styles.formFieldLabel}>{label}</RNText>
    <TextInput
      style={[
        styles.formFieldInput,
        multiline && { minHeight: 60, textAlignVertical: "top" },
      ]}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={colors.textSecondary}
      multiline={multiline}
      numberOfLines={multiline ? 3 : undefined}
    />
  </View>
);

const PickedFilesList = ({
  files,
  onRemove,
  styles,
}: {
  files: PickedFile[];
  onRemove: (id: string) => void;
  styles: Styles;
}) => {
  const { t } = useTranslation();
  if (files.length === 0) return null;
  return (
    <View style={{ gap: 6, marginBottom: 10 }}>
      {files.map((f) => (
        <View key={f.id} style={styles.mutedRow}>
          <RNText
            style={styles.mutedRowText}
            numberOfLines={1}
            ellipsizeMode="middle"
          >
            {f.fileName}
          </RNText>
          <TouchableOpacity onPress={() => onRemove(f.id)}>
            <RNText style={styles.mutedRowLink}>{t("remove")}</RNText>
          </TouchableOpacity>
        </View>
      ))}
    </View>
  );
};

const AttachmentsList = ({
  attachments,
  styles,
  colors,
}: {
  attachments?: Attachment[];
  styles: Styles;
  colors: any;
}) => {
  const { t } = useTranslation();
  if (!attachments || attachments.length === 0) {
    return <RNText style={styles.fieldValue}>-</RNText>;
  }
  return (
    <View style={{ gap: 8 }}>
      {attachments.map((a, idx) => {
        const showThumbnail = isImageFile(a.fileName) && !!a.fileUrl;

        return (
          <TouchableOpacity
            key={idx}
            style={styles.attachmentRow}
            disabled={!a.fileUrl}
            onPress={() => {
              if (a.fileUrl) Linking.openURL(a.fileUrl);
            }}
          >
            {showThumbnail ? (
              <Image
                source={{ uri: a.fileUrl }}
                style={styles.attachmentThumbnail}
                resizeMode="cover"
              />
            ) : (
              <View style={styles.attachmentIconBox}>
                <Ionicons
                  name="document-text-outline"
                  size={18}
                  color={colors.textSecondary}
                />
              </View>
            )}
            <RNText
              style={styles.attachmentName}
              numberOfLines={1}
              ellipsizeMode="middle"
            >
              {a.fileName}
            </RNText>
            <RNText style={styles.attachmentView}>{t("view")}</RNText>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

type FieldSpec = {
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
};

const FieldGrid = ({
  fields,
  styles,
}: {
  fields: FieldSpec[];
  styles: Styles;
}) => {
  const rows: FieldSpec[][] = [];
  let i = 0;
  while (i < fields.length) {
    if (fields[i].fullWidth) {
      rows.push([fields[i]]);
      i += 1;
      continue;
    }
    const pair = [fields[i]];
    if (fields[i + 1] && !fields[i + 1].fullWidth) {
      pair.push(fields[i + 1]);
      i += 2;
    } else {
      i += 1;
    }
    rows.push(pair);
  }
  return (
    <>
      {rows.map((row, idx) => (
        <View key={idx} style={styles.fieldRow}>
          {row.map((f, j) => (
            <View
              key={j}
              style={[styles.fieldCol, row.length === 1 && { flex: 1 }]}
            >
              <RNText style={styles.fieldLabel}>{f.label}</RNText>
              {typeof f.value === "string" || typeof f.value === "number" ? (
                <RNText style={styles.fieldValue}>{f.value}</RNText>
              ) : (
                f.value
              )}
            </View>
          ))}
        </View>
      ))}
    </>
  );
};

const DevRoleSwitcher = ({
  effectiveRole,
  onSelect,
  styles,
}: {
  effectiveRole: UserRole | null;
  onSelect: (role: UserRole) => void;
  styles: Styles;
}) => {
  const roles: UserRole[] = ["Sales", "Management", "China"];
  return (
    <View style={styles.devRoleSwitcher}>
      <RNText style={styles.devRoleSwitcherLabel}>Testing as:</RNText>
      {roles.map((role) => (
        <TouchableOpacity
          key={role}
          style={[
            styles.devRolePill,
            effectiveRole === role && styles.devRolePillActive,
          ]}
          onPress={() => onSelect(role)}
        >
          <RNText
            style={[
              styles.devRolePillText,
              effectiveRole === role && styles.devRolePillTextActive,
            ]}
          >
            {role}
          </RNText>
        </TouchableOpacity>
      ))}
    </View>
  );
};

interface SampleRequestDetailsScreenProps {
  navigation: any;
  route: { params: { request: SampleRequestDetail; requestId?: string } };
  onApprove?: (request: SampleRequestDetail) => void;
  onReject?: (request: SampleRequestDetail, reason: string) => void;
  onRequestChanges?: (
    request: SampleRequestDetail,
    remarks: string,
    requestedFor: "Sales" | "China",
  ) => void;
  onSubmitChanges?: (request: SampleRequestDetail) => void;
  onSubmitToFactory?: (
    request: SampleRequestDetail,
    toolingCost: string,
  ) => void;
  onApproveCAD?: (request: SampleRequestDetail) => void;
  onSubmitProduction?: (
    request: SampleRequestDetail,
    tracking: {
      trackingNumber: string;
      trackingLink?: string;
      courier?: string;
      shippedDate?: string | null;
    },
  ) => void;
  onConfirmReceived?: (request: SampleRequestDetail) => void;
  onEditRequest?: (request: SampleRequestDetail) => void;
}

export default function SampleRequestDetailsScreen({
  navigation,
  route,
  onApprove,
  onReject,
  onRequestChanges,
  onSubmitChanges,
  onSubmitToFactory,
  onApproveCAD,
  onSubmitProduction,
  onConfirmReceived,
  onEditRequest,
}: SampleRequestDetailsScreenProps) {
  const { colors, isDark } = useAppTheme();
  const styles = createStyles(colors, isDark);
  const toast = useToast();
  const { isSmallDevice } = useContext(DeviceOrientationContext);
  const dispatch = useDispatch<any>();
  const { t, language } = useTranslation();

  const translateValue = (value: string) =>
    translateFields(value, t, sampleRequestTranslationMap) || value;

  // Selected option is translated; free text typed under "Other" is kept
  // as entered. A legacy free-text purpose (not a known option) is shown the
  // same way, matching how the edit modal treats it.
  const formatPurpose = (purpose?: string, purposeOther?: string) => {
    if (!purpose) return "-";
    const isKnownOption =
      purpose !== "Other" &&
      !!translateFields(purpose, t, sampleRequestTranslationMap);
    if (isKnownOption) return translateValue(purpose);
    const otherText = purpose === "Other" ? purposeOther : purpose;
    const separator = language === "zh" ? "：" : ": ";
    return otherText ? `${t("other")}${separator}${otherText}` : t("other");
  };

  const translateActivity = (description: string) => {
    for (const { pattern, key } of sampleRequestActivityPatterns) {
      const match = pattern.exec(description);
      if (match) return t(key).replace("{value}", match[1]);
    }
    return translateValue(description);
  };

  const requestFromRoute = route.params.request;
  const requestId = route.params.requestId || (requestFromRoute as any)?._id;

  const selectedFromStore = useSelector(
    (state: any) => state.sampleRequest.selected,
  );

  const current: SampleRequestDetail =
    selectedFromStore && selectedFromStore._id === requestId
      ? selectedFromStore
      : requestFromRoute;

  useEffect(() => {
    if (requestId) {
      dispatch(getSampleRequestById(requestId));
    }
  }, [requestId]);

  const [promptAction, setPromptAction] = useState<
    null | "reject" | "requestChangesSales" | "requestChangesChina"
  >(null);

  const [toolingCostInput, setToolingCostInput] = useState("");
  const [factoryRemarksInput, setFactoryRemarksInput] = useState("");

  useEffect(() => {
    if (
      current?.status === "Sent to Factory" ||
      current?.status === "CAD Changes Requested"
    ) {
      setToolingCostInput(current?.mouldPreparation?.toolingCost || "");
      setFactoryRemarksInput(current?.mouldPreparation?.remarks || "");
    }
  }, [
    current?.status,
    current?.mouldPreparation?.toolingCost,
    current?.mouldPreparation?.remarks,
  ]);

  const [courierInput, setCourierInput] = useState("");
  const [trackingNumberInput, setTrackingNumberInput] = useState("");
  const [trackingLinkInput, setTrackingLinkInput] = useState("");
  const [shippedDateInput, setShippedDateInput] = useState("");
  const { userInfo } = useSelector((state: any) => state.configurations);
  const actorId = userInfo._id || userInfo.id;

  const [editModalVisible, setEditModalVisible] = useState(false);

  const [devRoleOverride, setDevRoleOverride] = useState<UserRole | null>(null);
  const effectiveRole = devRoleOverride;

  const canAct = (role: UserRole) => effectiveRole === role;

  const [toolingFiles, setToolingFiles] = useState<PickedFile[]>([]);
  const [productionFiles, setProductionFiles] = useState<PickedFile[]>([]);
  const [isUploadingTooling, setIsUploadingTooling] = useState(false);
  const [isUploadingProduction, setIsUploadingProduction] = useState(false);

  const pickFileWeb = (onPicked: (file: PickedFile) => void) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,application/pdf";
    input.style.position = "fixed";
    input.style.left = "-9999px";
    input.style.top = "0";

    const cleanup = () => {
      if (input.parentNode) input.parentNode.removeChild(input);
    };

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) {
        cleanup();
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        onPicked({
          id: Math.random().toString(36).substring(2, 15),
          fileName: file.name || `upload_${Date.now()}`,
          fileUri: reader.result as string,
          fileType: file.type || "application/octet-stream",
        });
        cleanup();
      };
      reader.onerror = () => {
        toast.show(t("couldNotReadSelectedFile"), { type: "danger" });
        cleanup();
      };
      reader.readAsDataURL(file);
    };

    document.body.appendChild(input);
    input.click();
  };

  const pickFileNative = async (onPicked: (file: PickedFile) => void) => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.All,
      quality: 0.5,
      base64: false,
    });
    if (!result.canceled) {
      const asset = result.assets[0];
      onPicked({
        id: Math.random().toString(36).substring(2, 15),
        fileName:
          asset.fileName ||
          `upload_${Date.now()}.${asset.uri.split(".").pop()}`,
        fileUri: asset.uri,
        fileType: asset.mimeType || "image/jpeg",
      });
    }
  };

  const pickFile = (onPicked: (file: PickedFile) => void) => {
    if (Platform.OS === "web") {
      pickFileWeb(onPicked);
    } else {
      pickFileNative(onPicked);
    }
  };

  const uploadFilesToS3 = async (
    files: PickedFile[],
    folder: string,
  ): Promise<Attachment[]> => {
    const uploaded: Attachment[] = [];
    for (const f of files) {
      const uniqueFileName = `${Date.now()}_${f.fileName.replace(/[\s,]+/g, "_")}`;
      const ok = await pushFileToS3(
        "POST",
        f.fileUri,
        uniqueFileName,
        f.fileType,
        folder,
      );
      if (!ok) {
        throw new Error(`Failed to upload ${f.fileName}`);
      }
      uploaded.push({
        fileName: uniqueFileName,
        uploadedBy: actorId,
      });
    }
    return uploaded;
  };

  const handleApprove = () => {
    dispatch(
      approveSampleRequest({
        id: requestId,
        actorId,
        actorName: userInfo.display_name,
      }),
    )
      .unwrap()
      .then((updated: any) => {
        onApprove?.(updated);
        toast.show(t("sampleRequestApproved"), { type: "success" });
      })
      .catch((err: any) => {
        toast.show(err?.message || t("failedToApproveRequest"), {
          type: "danger",
        });
      });
  };

  const handleReject = (reason: string) => {
    dispatch(
      rejectSampleRequest({
        id: requestId,
        actorId,
        actorName: userInfo.display_name,
        reason,
      }),
    )
      .unwrap()
      .then((updated: any) => {
        setPromptAction(null);
        onReject?.(updated, reason);
        toast.show(t("sampleRequestDeclined"), { type: "warning" });
      })
      .catch((err: any) => {
        toast.show(err?.message || t("failedToDeclineRequest"), {
          type: "danger",
        });
      });
  };

  const handleRequestChanges = (
    remarks: string,
    requestedFor: "Sales" | "China",
  ) => {
    dispatch(
      requestSampleRequestChanges({
        id: requestId,
        actorId,
        actorName: userInfo.display_name,
        remarks,
        requestedFor,
      }),
    )
      .unwrap()
      .then((updated: any) => {
        setPromptAction(null);
        onRequestChanges?.(updated, remarks, requestedFor);
        toast.show(t("changesRequestedSuccess"), { type: "success" });
      })
      .catch((err: any) => {
        toast.show(err?.message || t("failedToRequestChanges"), {
          type: "danger",
        });
      });
  };

  const handleSubmitChanges = () => {
    dispatch(
      submitSampleRequestChanges({
        id: requestId,
        actorId,
        actorName: userInfo.display_name,
      }),
    )
      .unwrap()
      .then((updated: any) => {
        onSubmitChanges?.(updated);
        toast.show(t("changesSubmitted"), { type: "success" });
      })
      .catch((err: any) => {
        toast.show(err?.message || t("failedToSubmitChanges"), {
          type: "danger",
        });
      });
  };

  const handleSubmitToFactory = async () => {
    try {
      setIsUploadingTooling(true);
      const attachments = await uploadFilesToS3(
        toolingFiles,
        sampleRequestS3Folder(),
      );

      dispatch(
        submitSampleRequestToFactory({
          id: requestId,
          actorId,
          actorName: userInfo.display_name,
          toolingCost: toolingCostInput.trim(),
          remarks: factoryRemarksInput.trim(),
          attachments,
        }),
      )
        .unwrap()
        .then((updated: any) => {
          const submittedCost = toolingCostInput.trim();
          setToolingCostInput("");
          setFactoryRemarksInput("");
          setToolingFiles([]);
          onSubmitToFactory?.(updated, submittedCost);
          toast.show(t("submittedToManagement"), {
            type: "success",
          });
        })
        .catch((err: any) => {
          toast.show(err?.message || t("failedToSubmit"), { type: "danger" });
        });
    } catch (err: any) {
      toast.show(err?.message || t("failedToUploadAttachment"), {
        type: "danger",
      });
    } finally {
      setIsUploadingTooling(false);
    }
  };

  const handleApproveCAD = () => {
    dispatch(
      approveSampleRequestCAD({
        id: requestId,
        actorId,
        actorName: userInfo.display_name,
      }),
    )
      .unwrap()
      .then((updated: any) => {
        onApproveCAD?.(updated);
        toast.show(t("cadDrawingApproved"), { type: "success" });
      })
      .catch((err: any) => {
        toast.show(err?.message || t("failedToApproveCad"), {
          type: "danger",
        });
      });
  };

  const handleSubmitProduction = async () => {
    if (!trackingNumberInput.trim()) {
      toast.show(t("trackingNumberRequired"), { type: "warning" });
      return;
    }
    if (productionFiles.length === 0) {
      toast.show(t("uploadEvidenceOfShipment"), { type: "warning" });
      return;
    }

    let shippedDate: string | null = null;
    if (shippedDateInput.trim()) {
      const parsed = parseDDMMYYYY(shippedDateInput);
      if (!parsed) {
        toast.show(t("shippedDateFormatError"), { type: "warning" });
        return;
      }
      shippedDate = parsed.toISOString();
    }

    const tracking = {
      trackingNumber: trackingNumberInput.trim(),
      trackingLink: normalizeUrl(trackingLinkInput),
      courier: courierInput.trim(),
      shippedDate,
    };

    try {
      setIsUploadingProduction(true);
      const attachments = await uploadFilesToS3(
        productionFiles,
        sampleRequestS3Folder(),
      );

      await dispatch(
        submitSampleRequestProduction({
          id: requestId,
          actorId,
          actorName: userInfo.display_name,
          ...tracking,
          attachments,
        }),
      )
        .unwrap()
        .then((updated: any) => {
          setProductionFiles([]);
          setCourierInput("");
          setTrackingNumberInput("");
          setTrackingLinkInput("");
          setShippedDateInput("");
          onSubmitProduction?.(updated, tracking);
          toast.show(t("markedAsShipped"), { type: "success" });
        });
    } catch (err: any) {
      toast.show(err?.message || t("failedToMarkAsShipped"), {
        type: "danger",
      });
    } finally {
      setIsUploadingProduction(false);
    }
  };

  const handleConfirmReceived = () => {
    dispatch(
      confirmSampleRequestReceived({
        id: requestId,
        actorId,
        actorName: userInfo.display_name,
      }),
    )
      .unwrap()
      .then((updated: any) => {
        onConfirmReceived?.(updated);
        toast.show(t("sampleRequestCompleted"), { type: "success" });
      })
      .catch((err: any) => {
        toast.show(err?.message || t("failedToConfirmReceipt"), {
          type: "danger",
        });
      });
  };

  const handleEditRequest = (data: {
    sampleInfo: Record<string, any>;
    deliveryInfo: Record<string, any>;
  }) => {
    if (current.status === "Declined") {
      toast.show(t("declinedCannotEdit"), {
        type: "warning",
      });
      setEditModalVisible(false);
      return;
    }

    dispatch(
      updateSampleRequest({
        id: requestId,
        actorId,
        actorName: userInfo.display_name,
        sampleInfo: data.sampleInfo,
        deliveryInfo: data.deliveryInfo,
      }),
    )
      .unwrap()
      .then((updated: any) => {
        setEditModalVisible(false);
        onEditRequest?.(updated);
        toast.show(t("sampleRequestUpdated"), { type: "success" });
      })
      .catch((err: any) => {
        toast.show(err?.message || t("failedToSaveChanges"), {
          type: "danger",
        });
      });
  };

  const renderAlertCard = () => {
    if (current.status === "Completed") return null;

    if (current.status === "Declined") {
      return (
        <View style={styles.alertCard}>
          <View style={styles.alertTopRow}>
            <View style={styles.alertTitleGroup}>
              <Ionicons name="close-circle-outline" size={18} color="#991B1B" />
              <RNText style={[styles.alertTitle, { color: "#991B1B" }]}>
                {t("declined")}
              </RNText>
            </View>
          </View>
          <View style={styles.alertBody}>
            <View style={[styles.alertBar, { backgroundColor: "#EF4444" }]} />
            <RNText style={styles.alertMessage}>
              {current.declinedReason || t("declinedByManagement")}
            </RNText>
          </View>
        </View>
      );
    }

    const messageKey = ALERT_MESSAGES[current.status];
    if (!messageKey) return null;
    const message = t(messageKey);

    let inlineActions: React.ReactNode = null;
    let extraContent: React.ReactNode = null;

    const renderFactoryForm = (submitLabel: string, uploadLabel: string) => (
      <>
        <FormField
          label={t("toolingCostOptional")}
          value={toolingCostInput}
          onChangeText={setToolingCostInput}
          placeholder={t("toolingCostPlaceholder")}
          styles={styles}
          colors={colors}
        />
        <FormField
          label={t("remarksOptional")}
          value={factoryRemarksInput}
          onChangeText={setFactoryRemarksInput}
          placeholder={t("notesForManagement")}
          multiline
          styles={styles}
          colors={colors}
        />
        <RNText style={styles.formFieldLabel}>{t("samplesOptional")}</RNText>
        <PickedFilesList
          files={toolingFiles}
          onRemove={(id) =>
            setToolingFiles((prev) => prev.filter((f) => f.id !== id))
          }
          styles={styles}
        />
        <TouchableOpacity
          style={styles.uploadBox}
          disabled={isUploadingTooling}
          onPress={() =>
            pickFile((f) => setToolingFiles((prev) => [...prev, f]))
          }
        >
          <RNText style={styles.uploadBoxText}>
            {isUploadingTooling ? t("uploadingEllipsis") : uploadLabel}
          </RNText>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.submitButton}
          onPress={handleSubmitToFactory}
          disabled={isUploadingTooling}
        >
          <RNText style={styles.submitButtonText}>
            {isUploadingTooling ? t("submittingEllipsis") : submitLabel}
          </RNText>
        </TouchableOpacity>
      </>
    );

    switch (current.status) {
      case "Pending Approval": {
        if (canAct("Management")) {
          inlineActions = (
            <View style={styles.alertActionsRow}>
              <ActionButton
                label={t("approve")}
                tone="approve"
                onPress={handleApprove}
                styles={styles}
              />
              <ActionButton
                label={t("reject")}
                tone="reject"
                onPress={() => setPromptAction("reject")}
                styles={styles}
              />
              <ActionButton
                label={t("requestChanges")}
                tone="neutral"
                onPress={() => setPromptAction("requestChangesSales")}
                styles={styles}
              />
            </View>
          );
        }
        if (promptAction === "reject") {
          extraContent = (
            <RemarksPrompt
              placeholder={t("reasonForDeclining")}
              confirmLabel={t("reject")}
              onConfirm={handleReject}
              onCancel={() => setPromptAction(null)}
              styles={styles}
              colors={colors}
            />
          );
        } else if (promptAction === "requestChangesSales") {
          extraContent = (
            <RemarksPrompt
              placeholder="What needs to change?"
              confirmLabel={t("sendToSales")}
              onConfirm={(text) => handleRequestChanges(text, "Sales")}
              onCancel={() => setPromptAction(null)}
              styles={styles}
              colors={colors}
            />
          );
        }
        break;
      }

      case "Changes Requested": {
        extraContent = (
          <>
            {current.activeChangeRequest && (
              <View style={styles.remarksBubble}>
                <RNText style={styles.remarksBubbleText}>
                  {t("remarksLabel")}: {current.activeChangeRequest.remarks}
                </RNText>
              </View>
            )}
            {canAct("Sales") && (
              <TouchableOpacity
                style={styles.submitButton}
                onPress={handleSubmitChanges}
              >
                <RNText style={styles.submitButtonText}>
                  {t("submitChanges")}
                </RNText>
              </TouchableOpacity>
            )}
          </>
        );
        break;
      }

      case "Sent to Factory": {
        if (canAct("China")) {
          extraContent = renderFactoryForm(
            t("submitForApproval"),
            t("uploadDrawingOrSpec"),
          );
        }
        break;
      }

      case "Reviewing CAD": {
        if (canAct("Management")) {
          inlineActions = (
            <View style={styles.alertActionsRow}>
              <ActionButton
                label={t("approve")}
                tone="approve"
                onPress={handleApproveCAD}
                styles={styles}
              />
              <ActionButton
                label={t("requestChanges")}
                tone="neutral"
                onPress={() => setPromptAction("requestChangesChina")}
                styles={styles}
              />
            </View>
          );
        }
        if (promptAction === "requestChangesChina") {
          extraContent = (
            <RemarksPrompt
              placeholder="What needs to change?"
              confirmLabel={t("sendToChina")}
              onConfirm={(text) => handleRequestChanges(text, "China")}
              onCancel={() => setPromptAction(null)}
              styles={styles}
              colors={colors}
            />
          );
        }
        break;
      }

      case "CAD Changes Requested": {
        extraContent = (
          <>
            {current.activeChangeRequest && (
              <View style={styles.remarksBubble}>
                <RNText style={styles.remarksBubbleText}>
                  {t("remarksLabel")}: {current.activeChangeRequest.remarks}
                </RNText>
              </View>
            )}
            {canAct("China") &&
              renderFactoryForm(
                t("resubmitForApproval"),
                t("uploadRevisedDrawingOrSpec"),
              )}
          </>
        );
        break;
      }

      case "In Production": {
        if (canAct("China")) {
          extraContent = (
            <>
              <FormField
                label={t("trackingNumberRequiredLabel")}
                value={trackingNumberInput}
                onChangeText={setTrackingNumberInput}
                styles={styles}
                colors={colors}
              />
              <FormField
                label={t("trackingLinkOptional")}
                value={trackingLinkInput}
                onChangeText={setTrackingLinkInput}
                placeholder={t("trackingLinkPlaceholder")}
                styles={styles}
                colors={colors}
              />
              <FormField
                label={t("courierOptional")}
                value={courierInput}
                onChangeText={setCourierInput}
                placeholder={t("courierPlaceholder")}
                styles={styles}
                colors={colors}
              />
              <FormField
                label={t("shippedDateOptional")}
                value={shippedDateInput}
                onChangeText={setShippedDateInput}
                placeholder={t("shippedDatePlaceholder")}
                styles={styles}
                colors={colors}
              />
              <RNText style={styles.formFieldLabel}>
                {t("evidenceOfShipmentRequiredLabel")}
              </RNText>
              <PickedFilesList
                files={productionFiles}
                onRemove={(id) =>
                  setProductionFiles((prev) => prev.filter((f) => f.id !== id))
                }
                styles={styles}
              />
              <TouchableOpacity
                style={styles.uploadBox}
                disabled={isUploadingProduction}
                onPress={() =>
                  pickFile((f) => setProductionFiles((prev) => [...prev, f]))
                }
              >
                <RNText style={styles.uploadBoxText}>
                  {isUploadingProduction
                    ? t("uploadingEllipsis")
                    : t("uploadEvidenceOfShipment")}
                </RNText>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.submitButton}
                onPress={handleSubmitProduction}
                disabled={isUploadingProduction}
              >
                <RNText style={styles.submitButtonText}>
                  {isUploadingProduction
                    ? t("submittingEllipsis")
                    : t("markAsShipped")}
                </RNText>
              </TouchableOpacity>
            </>
          );
        }
        break;
      }

      case "Shipped": {
        if (canAct("Sales")) {
          inlineActions = (
            <View style={styles.alertActionsRow}>
              <ActionButton
                label={t("sampleReceived")}
                tone="approve"
                onPress={handleConfirmReceived}
                styles={styles}
              />
            </View>
          );
        }
        break;
      }

      default:
        break;
    }

    return (
      <View style={styles.alertCard}>
        <View style={styles.alertTopRow}>
          <View style={styles.alertTitleGroup}>
            <Ionicons
              name="notifications-outline"
              size={18}
              color={colors.text}
            />
            <RNText style={styles.alertTitle}>{t("alert")}</RNText>
          </View>
          {inlineActions}
        </View>
        <View style={styles.alertBody}>
          <View style={styles.alertBar} />
          <RNText style={styles.alertMessage}>{message}</RNText>
        </View>
        {extraContent && (
          <View style={styles.alertExtraContent}>{extraContent}</View>
        )}
      </View>
    );
  };

  const renderSummaryDeliveryCard = () => (
    <View style={styles.sectionCard}>
      <RNText style={styles.sectionTitle}>{t("summaryInfo")}</RNText>
      <FieldGrid
        styles={styles}
        fields={[
          { label: t("customer"), value: current.sampleInfo.customer },
          {
            label: t("requestedBy"),
            value: current.requestedBy?.display_name || "-",
          },
          {
            label: t("dueDate"),
            value: formatDateLong(current.sampleInfo.dueDate),
          },
          {
            label: t("dateRaised"),
            value: formatDateLong((current as any).createdAt),
          },
          { label: t("itemName"), value: current.sampleInfo.itemName },
          {
            label: t("sampleQtyNeeded"),
            value: `${current.sampleInfo.sampleQtyNeeded} ${t("unitPcs")}`,
          },
          {
            label: t("purpose"),
            value: formatPurpose(
              current.sampleInfo.purpose,
              current.sampleInfo.purposeOther,
            ),
          },
          {
            label: t("descriptionOfSamples"),
            value: dash(current.sampleInfo.descriptionOfSample),
          },
          {
            label: t("attachments"),
            value: (
              <AttachmentsList
                attachments={current.sampleInfo.attachments}
                styles={styles}
                colors={colors}
              />
            ),
            fullWidth: true,
          },
        ]}
      />

      <View style={styles.sectionDivider} />

      <RNText style={styles.sectionTitle}>{t("deliveryInfo")}</RNText>
      <FieldGrid
        styles={styles}
        fields={[
          {
            label: t("deliveryContact"),
            value: `${current.deliveryInfo.deliveryContactPerson} | ${current.deliveryInfo.deliveryContactNumber}`,
          },
          {
            label: t("deliveryAddress"),
            value: current.deliveryInfo.deliveryAddress,
          },
          {
            label: t("remarksOfSamples"),
            value: dash(current.deliveryInfo.remarks),
            fullWidth: true,
          },
        ]}
      />
    </View>
  );

  const renderMouldTrackingCard = () => {
    const mould = current.mouldPreparation;
    const tracking = current.trackingInfo;
    return (
      <View style={styles.sectionCard}>
        <RNText style={styles.sectionTitle}>{t("mouldPreparation")}</RNText>
        <FieldGrid
          styles={styles}
          fields={[
            {
              label: t("drawingProvidedByFactory"),
              value:
                mould?.drawingProvidedByFactory === true
                  ? t("yes")
                  : mould?.drawingProvidedByFactory === false
                    ? t("no")
                    : "-",
            },
            {
              label: t("toolingRequestBy"),
              value: dash(mould?.toolingRequestBy),
            },
            { label: t("toolingCost"), value: dash(mould?.toolingCost) },
            {
              label: t("toolingApprovedBy"),
              value: dash(mould?.toolingApprovedBy),
            },
            {
              label: t("remarksLabel"),
              value: dash(mould?.remarks),
              fullWidth: true,
            },
            {
              label: t("attachments"),
              value: (
                <AttachmentsList
                  attachments={mould?.attachments}
                  styles={styles}
                  colors={colors}
                />
              ),
              fullWidth: true,
            },
          ]}
        />

        <View style={styles.sectionDivider} />

        <RNText style={styles.sectionTitle}>{t("trackingInfo")}</RNText>
        <FieldGrid
          styles={styles}
          fields={[
            { label: t("courier"), value: dash(tracking?.courier) },
            {
              label: t("trackingNumber"),
              value: dash(tracking?.trackingNumber),
            },
            {
              label: t("trackingLink"),
              value: tracking?.trackingLink ? (
                <TouchableOpacity
                  onPress={() => Linking.openURL(tracking.trackingLink!)}
                >
                  <RNText style={styles.attachmentView} numberOfLines={1}>
                    {t("trackShipment")}
                  </RNText>
                </TouchableOpacity>
              ) : (
                "-"
              ),
            },
            {
              label: t("shippedDate"),
              value: formatDateSlash(tracking?.shippedDate),
            },
            {
              label: t("evidenceOfShipment"),
              value: (
                <AttachmentsList
                  attachments={tracking?.attachments}
                  styles={styles}
                  colors={colors}
                />
              ),
              fullWidth: true,
            },
          ]}
        />
      </View>
    );
  };

  const renderActivitiesCard = () => (
    <View style={styles.activitiesCard}>
      <RNText style={styles.sectionTitle}>{t("activities")}</RNText>
      {current.activities.length === 0 ? (
        <RNText style={styles.fieldValue}>{t("noActivityYet")}</RNText>
      ) : (
        current.activities.map((activity, idx) => (
          <View key={idx} style={styles.activityItem}>
            <View style={styles.activityHeaderRow}>
              <View style={styles.activityRoleBadge}>
                <RNText style={styles.activityRoleBadgeText}>
                  {translateValue(activity.role)}
                </RNText>
              </View>
              <RNText style={styles.activityActorName}>
                {activity.actorName}
              </RNText>
              <View style={{ flex: 1 }} />
              <RNText style={styles.activityDate}>
                {formatDateLong(activity.date)}
              </RNText>
            </View>
            <RNText style={styles.activityDescription}>
              {translateActivity(activity.description)}
            </RNText>
          </View>
        ))
      )}
    </View>
  );

  const statusColors = statusColorMap[current.status] || {
    bg: colors.background,
    text: colors.textSecondary,
    dot: colors.textSecondary,
  };

  return (
    <View style={styles.pageWrapper}>
      <View style={styles.headerBar}>
        <TouchableOpacity
          onPress={() => navigation.navigate("SalesSampleRequestScreen")}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={20} color={colors.text} />
        </TouchableOpacity>
        <RNText style={styles.headerRequestId}>{current.requestId}</RNText>
        <View
          style={[
            styles.headerStatusPill,
            { backgroundColor: statusColors.bg },
          ]}
        >
          <RNText
            style={[styles.headerStatusText, { color: statusColors.text }]}
          >
            {STATUS_LABEL_OVERRIDES[current.status]
              ? t(STATUS_LABEL_OVERRIDES[current.status]!)
              : translateValue(current.status)}
          </RNText>
        </View>
        <View style={{ flex: 1 }} />
        {current.status !== "Declined" &&
          current.status === "Pending Approval" && (
            <TouchableOpacity
              style={styles.headerIconButton}
              onPress={() => setEditModalVisible(true)}
            >
              <Ionicons
                name="pencil-outline"
                size={18}
                color={colors.textSecondary}
              />
            </TouchableOpacity>
          )}
      </View>

      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.container}>
          <DevRoleSwitcher
            effectiveRole={effectiveRole}
            onSelect={setDevRoleOverride}
            styles={styles}
          />
          {renderAlertCard()}

          <View style={[styles.bodyRow, isSmallDevice && styles.bodyColumn]}>
            <View style={styles.leftColumn}>
              {renderSummaryDeliveryCard()}
              {renderMouldTrackingCard()}
            </View>
            <View style={styles.rightColumn}>{renderActivitiesCard()}</View>
          </View>
        </View>
      </ScrollView>

      {editModalVisible && (
        <EditSampleRequestModal
          visible={editModalVisible}
          onClose={() => setEditModalVisible(false)}
          request={current}
          onSubmit={handleEditRequest}
        />
      )}
    </View>
  );
}

function createStyles(colors: any, isDark: boolean) {
  return StyleSheet.create({
    pageWrapper: {
      flex: 1,
      backgroundColor: isDark ? colors.background : "#FFFFFF",
    },

    headerBar: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: 16,
      paddingVertical: 14,
      borderBottomWidth: 1,
      borderBottomColor: isDark ? colors.border : "#E2E8F0",
      gap: 12,
    },
    backButton: {
      padding: 4,
    },
    headerRequestId: {
      fontSize: 15,
      fontWeight: "700",
      color: isDark ? colors.text : "#0F172A",
    },
    headerStatusPill: {
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 999,
    },
    headerStatusText: {
      fontSize: 11,
      fontWeight: "700",
    },
    headerIconButton: {
      padding: 6,
      borderRadius: 8,
    },

    scrollView: {
      flex: 1,
    },
    container: {
      padding: 16,
      gap: 16,
    },

    devRoleSwitcher: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      backgroundColor: isDark ? colors.background : "#FFF7ED",
      borderWidth: 1,
      borderColor: "#FDBA74",
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 8,
      flexWrap: "wrap",
    },
    devRoleSwitcherLabel: {
      fontSize: 12,
      fontWeight: "700",
      color: "#9A3412",
      marginRight: 4,
    },
    devRolePill: {
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 999,
      backgroundColor: "#FFFFFF",
      borderWidth: 1,
      borderColor: "#FDBA74",
    },
    devRolePillActive: {
      backgroundColor: "#EA580C",
      borderColor: "#EA580C",
    },
    devRolePillText: {
      fontSize: 12,
      fontWeight: "600",
      color: "#9A3412",
    },
    devRolePillTextActive: {
      color: "#FFFFFF",
    },

    alertCard: {
      backgroundColor: isDark ? colors.surface : "#F7FAFC",
      borderRadius: 12,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      padding: 18,
      gap: 14,
    },
    alertTopRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 10,
    },
    alertTitleGroup: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },
    alertTitle: {
      fontSize: 15,
      fontWeight: "700",
      color: isDark ? colors.text : "#0F172A",
    },
    alertActionsRow: {
      flexDirection: "row",
      gap: 8,
      flexWrap: "wrap",
    },
    alertBody: {
      flexDirection: "row",
      gap: 12,
    },
    alertBar: {
      width: 3,
      borderRadius: 2,
      backgroundColor: isDark ? colors.border : "#CBD5E1",
    },
    alertMessage: {
      flex: 1,
      fontSize: 13,
      color: isDark ? colors.text : "#334155",
    },
    alertExtraContent: {
      gap: 4,
    },

    actionButtonBase: {
      paddingHorizontal: 16,
      paddingVertical: 9,
      borderRadius: 8,
    },
    actionButtonApprove: {
      backgroundColor: "#4CAF71",
    },
    actionButtonReject: {
      backgroundColor: "#EF7A6E",
    },
    actionButtonNeutral: {
      backgroundColor: "#D6C452",
    },
    actionButtonText: {
      color: "#FFFFFF",
      fontSize: 13,
      fontWeight: "700",
    },

    submitButton: {
      backgroundColor: isDark ? colors.primary : "#2F95DC",
      borderRadius: 8,
      paddingVertical: 12,
      alignItems: "center",
      marginTop: 4,
    },
    submitButtonText: {
      color: "#FFFFFF",
      fontSize: 14,
      fontWeight: "700",
    },

    formFieldWrapper: {
      marginBottom: 4,
    },
    formFieldLabel: {
      fontSize: 13,
      fontWeight: "500",
      color: isDark ? colors.textSecondary : "#374151",
      marginBottom: 6,
    },
    formFieldInput: {
      backgroundColor: isDark ? colors.background : "#FFFFFF",
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 13,
      color: isDark ? colors.text : "#0F172A",
    },

    mutedRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: isDark ? colors.background : "#EEF2F6",
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
    },
    mutedRowText: {
      fontSize: 13,
      color: isDark ? colors.text : "#334155",
      flex: 1,
      marginRight: 8,
    },
    mutedRowLink: {
      fontSize: 13,
      fontWeight: "600",
      color: isDark ? colors.primary : "#2F95DC",
    },

    uploadBox: {
      backgroundColor: isDark ? colors.background : "#F8FAFC",
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      borderStyle: "dashed",
      paddingVertical: 14,
      alignItems: "center",
    },
    uploadBoxText: {
      fontSize: 12,
      color: colors.textSecondary,
    },

    remarksBubble: {
      backgroundColor: isDark ? colors.background : "#E5E7EB",
      borderRadius: 999,
      paddingHorizontal: 16,
      paddingVertical: 10,
      alignSelf: "flex-start",
    },
    remarksBubbleText: {
      fontSize: 13,
      color: isDark ? colors.text : "#374151",
    },

    remarksPromptBox: {
      gap: 10,
    },
    remarksPromptInput: {
      backgroundColor: isDark ? colors.background : "#FFFFFF",
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 13,
      minHeight: 60,
      textAlignVertical: "top",
      color: isDark ? colors.text : "#0F172A",
    },
    remarksPromptActions: {
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: 8,
    },
    remarksCancelBtn: {
      paddingHorizontal: 14,
      paddingVertical: 9,
      borderRadius: 8,
      backgroundColor: isDark ? colors.background : "#E2E8F0",
    },
    remarksCancelText: {
      fontSize: 13,
      fontWeight: "600",
      color: isDark ? colors.text : "#334155",
    },
    remarksConfirmBtn: {
      paddingHorizontal: 14,
      paddingVertical: 9,
      borderRadius: 8,
      backgroundColor: isDark ? colors.primary : "#2F95DC",
    },
    remarksConfirmText: {
      fontSize: 13,
      fontWeight: "700",
      color: "#FFFFFF",
    },

    bodyRow: {
      flexDirection: "row",
      gap: 16,
      alignItems: "flex-start",
    },
    bodyColumn: {
      flexDirection: "column",
    },
    leftColumn: {
      flex: 2,
      gap: 16,
      minWidth: 0,
    },
    rightColumn: {
      flex: 1,
      minWidth: 0,
    },

    sectionCard: {
      backgroundColor: isDark ? colors.surface : "#F7FAFC",
      borderRadius: 12,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      padding: 18,
    },
    sectionTitle: {
      fontSize: 15,
      fontWeight: "700",
      color: isDark ? colors.text : "#0F172A",
      marginBottom: 14,
    },
    sectionDivider: {
      height: 1,
      backgroundColor: isDark ? colors.border : "#E2E8F0",
      marginVertical: 16,
    },

    fieldRow: {
      flexDirection: "row",
      gap: 20,
      marginBottom: 16,
    },
    fieldCol: {
      flex: 1,
      minWidth: 0,
    },
    fieldLabel: {
      fontSize: 12,
      fontWeight: "600",
      color: isDark ? colors.textSecondary : "#64748B",
      marginBottom: 4,
    },
    fieldValue: {
      fontSize: 13,
      color: isDark ? colors.text : "#0F172A",
    },

    attachmentRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },
    attachmentThumbnail: {
      width: 32,
      height: 32,
      borderRadius: 4,
      backgroundColor: isDark ? colors.background : "#F1F5F9",
    },
    attachmentIconBox: {
      width: 32,
      height: 32,
      borderRadius: 4,
      backgroundColor: isDark ? colors.background : "#F1F5F9",
      alignItems: "center",
      justifyContent: "center",
    },
    attachmentName: {
      fontSize: 13,
      color: isDark ? colors.text : "#334155",
      flex: 1,
      flexShrink: 1,
    },
    attachmentView: {
      fontSize: 13,
      fontWeight: "600",
      color: isDark ? colors.primary : "#2F95DC",
    },

    activitiesCard: {
      backgroundColor: isDark ? colors.surface : "#F7FAFC",
      borderRadius: 12,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      padding: 18,
    },
    activityItem: {
      borderLeftWidth: 2,
      borderLeftColor: isDark ? colors.border : "#CBD5E1",
      paddingLeft: 12,
      marginBottom: 16,
    },
    activityHeaderRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      marginBottom: 4,
    },
    activityRoleBadge: {
      backgroundColor: "#94A3B8",
      borderRadius: 4,
      paddingHorizontal: 6,
      paddingVertical: 2,
    },
    activityRoleBadgeText: {
      fontSize: 10,
      fontWeight: "700",
      color: "#FFFFFF",
    },
    activityActorName: {
      fontSize: 13,
      fontWeight: "700",
      color: isDark ? colors.text : "#0F172A",
    },
    activityDate: {
      fontSize: 12,
      color: isDark ? colors.textSecondary : "#64748B",
    },
    activityDescription: {
      fontSize: 13,
      color: isDark ? colors.textSecondary : "#475569",
    },
  });
}
